import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Users, Calendar, DollarSign, ArrowRight, Check } from "lucide-react";
import dashboardImg from "@/assets/dashboard-screenshot.png";
import membersImg from "@/assets/members-screenshot.png";

const LandingOption3 = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Split Screen */}
      <section className="relative bg-background py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            <div className="space-y-8 animate-fade-in">
              <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight">
                The Modern Way to
                <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  Manage Your PTA
                </span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Streamline member management, event planning, and fundraising with powerful tools built specifically for school PTAs.
              </p>
              <div className="space-y-4">
                {["No setup fees", "14-day free trial", "Cancel anytime"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="h-6 w-6 text-primary shrink-0" />
                    <span className="text-lg text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 pt-4">
                <Link to="/auth">
                  <Button size="lg" className="shadow-elevated text-lg px-10 py-6">
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="text-lg px-10 py-6">
                    See Pricing
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <Card className="overflow-hidden shadow-elevated">
                <img src={dashboardImg} alt="PTA Hub Dashboard" className="w-full" />
              </Card>
              <div className="absolute -bottom-6 -left-6 bg-card border-2 border-border rounded-2xl p-6 shadow-elevated max-w-xs">
                <div className="text-4xl font-bold text-primary mb-1">500+</div>
                <div className="text-sm text-muted-foreground">PTAs using PTA Hub</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive tools designed for modern PTAs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="shadow-card border-2">
              <CardHeader>
                <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-primary-foreground" />
                </div>
                <CardTitle className="text-2xl">Member Management</CardTitle>
                <CardDescription className="text-base pt-2">
                  Keep your PTA organized with powerful member management tools. Track roles, permissions, and engagement in one place.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-card border-2">
              <CardHeader>
                <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8 text-primary-foreground" />
                </div>
                <CardTitle className="text-2xl">Event Planning</CardTitle>
                <CardDescription className="text-base pt-2">
                  Plan and coordinate all your PTA events seamlessly. From school fairs to fundraisers, keep everything on track.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-card border-2">
              <CardHeader>
                <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-4">
                  <DollarSign className="h-8 w-8 text-primary-foreground" />
                </div>
                <CardTitle className="text-2xl">Fundraising</CardTitle>
                <CardDescription className="text-base pt-2">
                  Track donations and manage fundraising campaigns with ease. Transparent financial tools built for PTAs.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Visual Proof */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Card className="overflow-hidden shadow-elevated">
                <img src={membersImg} alt="Member Management" className="w-full" />
              </Card>
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-foreground">
                Built for School Communities
              </h2>
              <p className="text-lg text-muted-foreground">
                We understand the unique challenges PTAs face. Our platform is designed from the ground up to address your specific needs.
              </p>
              <ul className="space-y-4">
                {[
                  "Save 10+ hours per week on admin",
                  "Increase parent engagement by 50%",
                  "GDPR compliant and secure",
                  "Dedicated support team"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="h-6 w-6 text-primary shrink-0" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            Ready to Transform Your PTA?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of PTAs already using PTA Hub to streamline operations
          </p>
          <Link to="/auth">
            <Button size="lg" className="shadow-elevated text-xl px-12 py-8">
              Start Your Free Trial
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Label */}
      <div className="fixed top-20 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg font-bold z-50">
        Option 3
      </div>
    </div>
  );
};

export default LandingOption3;
